import React from 'react';
import Highcharts from 'highcharts/highcharts';
import HighchartsReact from 'highcharts-react-official';

import { InlineMath } from 'react-katex';
import 'katex/dist/katex.min.css';

import dnf_orig from './dnf_orig';
import dnf_adj from './dnf_adj';
import ig_kick from './ig_kick';
import ig_adj from './ig_adj';
import up_orig from './up_orig';
import up_adj1 from './up_adj1';
import up_adj2 from './up_adj2';

const base = {
  credits: {
    enabled: false
  },
  plotOptions: {
    series: {
      enableMouseTracking: false,
      lineWidth: 1,
      marker: { enabled: true }
    }
  },
}

const ig1 = {
  ...base,
  yAxis: {
    title: { enabled: false },
    min: 9000,
    max: 14000,
    tickInterval: 1000
  },
  title: {
    text: 'Insulin Glucose',
    margin: 1,
    style: { fontSize: '12px' },
  },
  legend: {
    enabled: false,
  },
  series: [
    {
      marker: { enabled: true, radius: 2 },
      data: ig_kick,
    }
  ]
};

const ig2 = {
  ...base,
  yAxis: {
    title: { enabled: false },
    min: -0.2,
    max: 0.3,
    tickInterval: .1
  },
  title: {
    text: 'Insulin Glucose Rescaled',
    margin: 1,
    style: { fontSize: '12px' },
  },
  legend: {
    enabled: false,
  },
  series: [
    {
      marker: { enabled: true, radius: 2 },
      data: ig_adj,
    }
  ]
};

const dnf1 = {
  ...base,
  yAxis: {
    title: { enabled: false },
    min: -20,
    max: 220,
    tickInterval: 20
  },
  xAxis: {
    min: 0,
    max: 15000
  },
  title: {
    text: 'Original',
    margin: 1,
    style: { fontSize: '12px' },
  },
  series: [
    {
      marker: { enabled: true, radius: 2 },
      data: dnf_orig,
    }
  ]
};

const dnf2 = {
  ...base,
  yAxis: {
    title: { enabled: false },
    min: -4,
    max: 8,
    tickInterval: 2
  },
  xAxis: {
    min: 0,
    max: 15000
  },
  title: {
    text: 'Rescaled',
    margin: 1,
    style: { fontSize: '12px' },
  },
  series: [
    {
      marker: { enabled: true, radius: 2 },
      data: dnf_adj,
    }
  ]
};

const upBase = {
  credits: {
    enabled: false
  },
  plotOptions: {
    series: {
      enableMouseTracking: false,
      lineWidth: 1,
      marker: { enabled: true }
    }
  },
  yAxis: {
    title: { enabled: false },
    min: -0.2,
    max: 0.3,
    tickInterval: 0.1
  }
};

const up1 = {
  ...upBase,
  title: {
    text: 'Upsampling 2x',
    margin: 1,
    style: { fontSize: '12px' },
  },
  series: [
    {
      marker: { enabled: true, radius: 3 },
      data: up_orig.slice(0, 100),
      name: 'Original Training Data'
    },
    {
      marker: { enabled: true, radius: 3 },
      data: up_adj1.slice(0, 50),
      name: 'Resampled Data'
    }
  ]
};

const up2 = {
  ...base,
  title: {
    text: 'Upsampling 4x',
    margin: 1,
    style: { fontSize: '12px' },
  },
  series: [
    {
      marker: { enabled: true, radius: 3 },
      data: up_orig.slice(0, 100),
      name: 'Original Training Data'
    },
    {
      marker: { enabled: true, radius: 3 },
      data: up_adj2.slice(0, 25),
      name: 'Resampled Data'
    }
  ]
};

const igOpts = { style: { height: '250px', width:'100%' }};
const upOpts = { style: { height: '250px', width:'100%' }};

function InputScaling() {
  return (
    <div id="Input_scaling" className="row">
      <h3>Input Scaling</h3>
      <div className="w-100">
        <p>
          Unsurprisingly, we observed that large numbers easily saturated the reservoir units.
          To correct for saturation and reduce noise, we prepare the data through rescaling and upsampling.
        </p>
        <p>
          Our reservoir consists of <code>n_units</code>, representing the number neurons in the network.
          We use <code>numpy</code> to generate our random state.
        </p>
      </div>
      <h5>Rescaling</h5>
      <div className="w-100">
        <p>The degrade-and-fire model displays clustering and excess amplitude. To compensate, we rescale the data using <InlineMath>{` \\ln(r) `}</InlineMath></p>
      </div>
      <HighchartsReact
        highcharts={Highcharts}
        options={dnf1}
        containerProps={igOpts}
      />
      <HighchartsReact
        highcharts={Highcharts}
        options={dnf2}
        containerProps={igOpts}
      />
      <div className="w-100">
        <p>
          We rescale the data by a factor of <InlineMath>{` 10^{-4} `}</InlineMath> and then shift by <InlineMath>-1.1</InlineMath> to correct for excess amplitude.
        </p>
      </div>
      <HighchartsReact
        highcharts={Highcharts}
        options={ig1}
        containerProps={igOpts}
      />
      <HighchartsReact
        highcharts={Highcharts}
        options={ig2}
        containerProps={igOpts}
      />
      <h5>Upsampling</h5>
      <div className="w-100">
        <p>The arbitrary long periods of oscillations in the degrade-and-fire model compared to delay time are especially difficult to predict. Upsampling by a small factor helps mitigate clustering points and noise while closely tracking the underlying data.</p>
      </div>
      <HighchartsReact
        highcharts={Highcharts}
        options={up1}
        containerProps={upOpts}
      />
      <HighchartsReact
        highcharts={Highcharts}
        options={up2}
        containerProps={upOpts}
      />
    </div>
  );
}

export default InputScaling;
